import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useAuth } from '../contexts/AuthContextRedux'
import { useToast } from '../contexts/ToastContext'
import { searchAPI } from '../services/api'
import ReduxWebSocketManager from '../services/reduxWebsocket'
import { store } from '../store'
import PromptInput from './PromptInput'
import StreamWidgets from './StreamWidgets'
import ChatMessages from './ChatMessages'
import ChatHistorySidebar from './ChatHistorySidebar'
import { logError } from './ErrorLogger'

// Redux actions
import {
  createChatSession,
  setActiveSession,
  fetchAnswer,
  fetchConversationMessages,
  updateChatSession
} from '../actions/chatActions'
import { CREATE_CHAT_SESSION } from '../actions/types'
import {
  sendMessage,
  updateMessage,
  clearStreamData
} from '../actions/messageActions'

export default function ChatLayoutRedux() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { token, logout, user, isAuthenticated } = useAuth()
  const { addToast } = useToast()
  const dispatch = useDispatch()
  
  // Redux state
  const { 
    activeSession, 
    sessions, 
    loading: chatLoading 
  } = useSelector(state => state.chat)
  const { 
    streamData, 
    loading: messageLoading,
    messagesBySession,
    messages
  } = useSelector(state => state.messages)
  const { 
    status: wsStatus 
  } = useSelector(state => state.websocket)
  
  // Local state
  const [prompt, setPrompt] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  
  // WebSocket manager
  const wsManagerRef = useRef(null)
  
  // Initialize WebSocket manager
  useEffect(() => {
    if (!wsManagerRef.current) {
      wsManagerRef.current = new ReduxWebSocketManager(store)
      
      // Expose WebSocket manager to window for debugging
      if (typeof window !== 'undefined') {
        window.wsManager = wsManagerRef.current
        console.log('🔧 Debug: wsManager available at window.wsManager')
        console.log('  - Reset document count: window.wsManager.resetDocumentCount()')
        console.log('  - Get current count: window.wsManager.getPersistentDocumentCount()')
      }
    }
    
    return () => {
      if (wsManagerRef.current) {
        wsManagerRef.current.disconnectAll()
      }
    }
  }, [])
  
  // Handle session ID from URL
  useEffect(() => {
    if (sessionId) {
      dispatch(setActiveSession(sessionId))
      
      // Check if this is a session we should load from API
      const sessionInStore = sessions[sessionId]
      const hasMessages = messagesBySession[sessionId] && messagesBySession[sessionId].length > 0
      
      console.log('🔍 Session ID from URL:', sessionId)
      console.log('📋 Session in store:', !!sessionInStore)
      console.log('💬 Has messages:', hasMessages)
      
      // Only try to load from API if:
      // 1. Session exists in our sessions store (from chat history)
      // 2. AND it doesn't have messages yet (hasn't been loaded)
      // 3. OR if it's a very recent session (less than 1 minute old)
      const isRecentSession = sessionInStore && sessionInStore.createdAt && 
        (new Date() - new Date(sessionInStore.createdAt)) < 60000
      
      if (sessionInStore && !hasMessages && !isRecentSession) {
        console.log('📂 Loading historical session from API:', sessionId)
        loadSession(sessionId)
      } else {
        console.log('⚠️ Skipping API load for session:', sessionId)
        if (!sessionInStore) {
          console.log('  - Reason: Session not in store (likely new WebSocket session)')
        } else if (hasMessages) {
          console.log('  - Reason: Session already has messages')
        } else if (isRecentSession) {
          console.log('  - Reason: Session is recent (likely from WebSocket)')
        }
      }
    }
  }, [sessionId, sessions, messagesBySession, dispatch])
  
  // Load session from API
  const loadSession = async (id) => {
    try {
      console.log('📤 Loading session from API:', id)
      await dispatch(fetchConversationMessages(id))
      dispatch(setActiveSession(id))
      console.log('✅ Session loaded successfully')
    } catch (error) {
      console.error('❌ Failed to load session:', error)
      if (error.response?.status === 404) {
        // Don't show error toast for 404s - this is expected for new sessions
        console.log('📭 Session not found in API, continuing with empty session')
      } else if (error.response?.status === 401) {
        console.error('Authentication error loading session:', error)
        // Don't logout here - let the user try again
      } else {
        addToast('Failed to load session', 'error')
        navigate('/chat')
      }
    }
  }
  
  // Handle message submission
  const handleSubmit = async (submittedPrompt) => {
    if (!submittedPrompt.trim() || messageLoading) return
    
    console.log('🚀 handleSubmit called with prompt:', submittedPrompt.substring(0, 50) + '...')
    console.log('📊 Auth state:', { 
      hasToken: !!token, 
      tokenLength: token?.length,
      isAuthenticated,
      user: user?.email 
    })
    console.log('🔑 LocalStorage tokens:', {
      authToken: localStorage.getItem('authToken')?.substring(0, 20) + '...',
      sessionToken: localStorage.getItem('sessionToken')?.substring(0, 20) + '...',
      user: localStorage.getItem('user')
    })
    
    try {
      // Validate authentication
      if (!token) {
        addToast('Please log in to start a research session', 'error')
        console.error('No token available - user needs to log in')
        return
      }
      
      // Ensure we have an active session
      let currentSessionId = activeSession
      if (!currentSessionId) {
        const sessionAction = createChatSession(submittedPrompt)
        currentSessionId = sessionAction(dispatch)
        console.log('🆕 Created new session, navigating to:', `/chat/${currentSessionId}`)
        navigate(`/chat/${currentSessionId}`, { replace: true })
      } else {
        // Ensure URL matches the active session
        if (window.location.pathname !== `/chat/${currentSessionId}`) {
          console.log('🔄 Updating URL to match active session:', `/chat/${currentSessionId}`)
          navigate(`/chat/${currentSessionId}`, { replace: true })
        }
      }
      
      // Add user message and prepare for response first (with current session)
      const { userMessageId, assistantMessageId } = dispatch(sendMessage(submittedPrompt, currentSessionId))
      
      // Call search API
      console.log('📡 Calling search API with prompt:', submittedPrompt)
      const response = await searchAPI.startSearch(submittedPrompt)
      console.log('📥 Search API response:', response)
      
      if (!response || !response.ws_url) {
        throw new Error('Invalid response from search endpoint - missing ws_url')
      }
      
      // Extract the backend session ID from WebSocket URL  
      const backendSessionId = response.ws_url.split('/').pop()
      console.log('🔗 Backend generated session ID:', backendSessionId, 'Frontend session ID:', currentSessionId)
      
      // If backend session ID is different, we need to migrate to backend session
      if (backendSessionId !== currentSessionId) {
        console.log('🔄 Migrating to backend session ID:', backendSessionId)
        
        // Update the URL to use backend session ID
        navigate(`/chat/${backendSessionId}`, { replace: true })
        
        // Set backend session as active
        dispatch(setActiveSession(backendSessionId))
        
        // Create session entry for backend session ID
        dispatch({
          type: CREATE_CHAT_SESSION,
          payload: {
            id: backendSessionId,
            title: submittedPrompt,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        })
        
        // Move messages from old session to new session
        dispatch({
          type: 'MIGRATE_SESSION_MESSAGES',
          payload: {
            oldSessionId: currentSessionId,
            newSessionId: backendSessionId,
            messageIds: [userMessageId, assistantMessageId]
          }
        })
        
        // Use backend session ID going forward
        currentSessionId = backendSessionId
      }
      
      // Update session title
      dispatch(updateChatSession(currentSessionId, { 
        title: submittedPrompt,
        updatedAt: new Date().toISOString()
      }))
      
      // Connect to WebSocket with the session ID
      if (wsManagerRef.current) {
        wsManagerRef.current.connect(currentSessionId, response.ws_url, token)
      }
      
      setPrompt('')
      
    } catch (error) {
      console.error('Search error:', error)
      
      // Handle different error types
      if (error.response?.status === 401) {
        addToast('Authentication failed. Please log in again.', 'error')
        logout()
      } else if (error.response?.status === 400) {
        addToast(error.response?.data?.detail || 'Invalid search query', 'error')
      } else if (error.response?.status >= 500) {
        addToast('Server error. Please try again later.', 'error')
      } else {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to start research'
        addToast(errorMessage, 'error')
      }
      
      logError('Search initiation failed', { 
        error: error.toString(),
        status: error.response?.status,
        response: error.response?.data
      })
    }
  }
  
  // Handle new chat
  const handleNewChat = () => {
    const newSessionId = dispatch(createChatSession())
    navigate(`/chat/${newSessionId}`)
    setPrompt('')
    dispatch(clearStreamData())
  }
  
  // Get current session messages
  const currentSessionMessages = activeSession 
    ? (messagesBySession[activeSession] || []).map(id => messages[id]).filter(Boolean)
    : []
  
  // Debug current session messages (only log if there are issues)
  if (currentSessionMessages.length === 0 && activeSession) {
    console.log('⚠️ Active session but no messages:', {
      activeSession,
      messageIds: messagesBySession[activeSession] || [],
      messageCount: currentSessionMessages.length
    })
  }
  
  // Loading state
  const loading = messageLoading || chatLoading
  
  return (
    <div className="flex h-screen bg-base-200">
      {/* Sidebar */}
      <ChatHistorySidebar 
        isOpen={showHistory} 
        onClose={() => setShowHistory(false)}
        onNewChat={handleNewChat}
      />
      
      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-base-100 shadow-sm p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="btn btn-ghost btn-sm"
                title="Toggle History"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="font-semibold" style={{fontSize: '16px', marginRight: '24px'}}>
                {activeSession && sessions[activeSession]?.title || 'New Chat'}
              </h1>
            </div>
            <div className="flex items-center space-x-6 mr-6">
              <button
                onClick={handleNewChat}
                className="btn btn-outline btn-sm"
              >
                New Chat
              </button>
              {user?.email && (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    {user.email}
                  </span>
                  <button
                    onClick={logout}
                    className="btn btn-ghost btn-sm text-error hover:bg-error hover:text-error-content ml-2"
                    title="Logout"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Chat content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Messages area - integrated with stream data */}
          <div className="flex-1 overflow-y-auto p-4">
            <ChatMessages 
              messages={currentSessionMessages}
              loading={loading}
              streamData={streamData}
              wsStatus={wsStatus}
            />
          </div>
          
          {/* Input */}
          <div className="flex-shrink-0 p-4 border-t bg-base-100">
            <PromptInput
              value={prompt}
              onChange={setPrompt}
              onSubmit={handleSubmit}
              disabled={loading}
              placeholder="Ask a research question..."
            />
          </div>
        </div>
      </div>
    </div>
  )
}